/**
 * ============================================================================
 * TIME SEGMENTATION - Day View Time Bucketing Logic
 * ============================================================================
 *
 * This file handles the logic for splitting the day into time segments
 * based on the selected day view mode.
 *
 * DAY VIEW MODES:
 * 1. Full Day - No segmentation, all items shown together
 * 2. Hourly - Items split into hour buckets (08:00-20:00)
 * 3. Custom - User-defined time segments (e.g., Morning/Afternoon)
 *
 * KEY CONCEPTS:
 * - TimeSegment: A time boundary/split point in the day
 * - Bucket: A container for items that fall within a time range
 * - For hourly view: Each hour becomes its own bucket
 *
 * BUCKET ID FORMAT:
 * - "full-day": Single bucket for all items
 * - "before-8": Items scheduled before 8 AM
 * - "hour-8", "hour-9", etc.: Items for specific hours
 * - "after-20": Items scheduled after 8 PM
 */
import { DayView, HourlyTaskCard } from "../types/types";

/**
 * Represents a time segment/split point in a day view.
 *
 * @property id - Unique identifier for this segment
 * @property splitTime - The actual time string (e.g., "08:00")
 * @property label - Display label for UI rendering
 */
export type TimeSegment = {
  id: string;
  splitTime: string; // The actual split/break time like "08:00"
  label: string; // Display label showing just the split time
};

export const HOURLY_DAYVIEW_ID = "bd727a0d-e28c-40b0-b4bc-097ab3981bd8";
export const DAILY_DAYVIEW_ID = "cb16f1a2-197a-4689-87b0-54d7c8961a88";

export type DayViewMode = "hourly" | "daily" | "custom";

export function getDayViewMode(dayView: DayView | null): DayViewMode {
  if (!dayView) return "daily";
  if (dayView.dayViewId === HOURLY_DAYVIEW_ID) return "hourly";
  if (dayView.dayViewId === DAILY_DAYVIEW_ID) return "daily";
  return "custom";
}

/**
 * Generates time segments based on the selected day view.
 *
 * LOGIC BY VIEW TYPE:
 * 1. No day view / Full Day: Returns single empty segment
 * 2. Hourly: Returns segments for hours 08:00 through 20:00 (13 segments)
 * 3. Custom: Uses splitTimes from the DayView configuration
 *
 * These segments are used to:
 * - Render time labels between task groups
 * - Determine which bucket each item belongs to
 *
 * @param dayView - The currently selected day view (can be null)
 * @returns Array of TimeSegment objects
 *
 * @example
 * // Full Day
 * generateTimeSegments(fullDayView)
 * // Returns: [{ id: "full-day", splitTime: "", label: "" }]
 *
 * @example
 * // Hourly
 * generateTimeSegments(hourlyView)
 * // Returns: [
 * //   { id: "hour-8", splitTime: "08:00", label: "08:00" },
 * //   { id: "hour-9", splitTime: "09:00", label: "09:00" },
 * //   ... through hour-20
 * // ]
 */
export function generateTimeSegments(dayView: DayView | null): TimeSegment[] {
  const mode = getDayViewMode(dayView);

  // Full Day view - single segment with no time boundary
  if (mode === "daily") {
    return [{
      id: "full-day",
      splitTime: "",
      label: ""
    }];
  }

  // Hourly view - create segment for each hour from 08:00 to 20:00
  // This creates time boundaries that items are sorted into
  if (mode === "hourly") {
    const segments: TimeSegment[] = [];
    for (let hour = 8; hour <= 20; hour++) {
      const splitTime = `${hour.toString().padStart(2, "0")}:00`;
      segments.push({
        id: `hour-${hour}`,
        splitTime,
        label: splitTime
      });
    }
    return segments;
  }

  // Custom day view - use splitTimes from configuration
  // These are store-specific segments like "Morning", "Afternoon"
  if (dayView?.splitTimes && dayView.splitTimes.length > 0) {
    const segments: TimeSegment[] = [];

    dayView.splitTimes.forEach((st, index) => {
      const hour = parseInt(st.splitTime.split(":")[0]);
      const splitTime = `${hour.toString().padStart(2, "0")}:00`;

      segments.push({
        id: `custom-${index}`,
        splitTime,
        label: splitTime
      });
    });

    // Sort by time for consistent ordering
    segments.sort((a, b) => a.splitTime.localeCompare(b.splitTime));

    return segments;
  }

  // Fallback to full day
  return [{
    id: "full-day",
    splitTime: "",
    label: ""
  }];
}

/**
 * Determines which time bucket an hour belongs to.
 *
 * BUCKET LOGIC (for segments 08:00 to 20:00):
 * - hour < 8  -> "before-8"   (early morning)
 * - hour = 8  -> "hour-8"     (8:00-8:59)
 * - hour = 9  -> "hour-9"     (9:00-9:59)
 * - ...
 * - hour = 19 -> "hour-19"    (19:00-19:59)
 * - hour >= 20 -> "after-20"  (evening)
 *
 * @param hour - Hour value (0-23)
 * @param segments - Array of time segments defining boundaries
 * @returns Bucket ID string
 */
function getTimeBucketId(hour: number, segments: TimeSegment[]): string {
  if (segments.length === 0 || (segments.length === 1 && segments[0].id === "full-day")) {
    return "full-day";
  }

  // Parse segment hours (sorted)
  const segmentHours = segments
    .map(s => parseInt(s.splitTime.split(":")[0], 10))
    .filter(h => !isNaN(h))
    .sort((a, b) => a - b);

  if (segmentHours.length === 0) return "full-day";

  const firstSegmentHour = segmentHours[0];
  const lastSegmentHour = segmentHours[segmentHours.length - 1];

  // Before first segment
  if (hour < firstSegmentHour) {
    return `before-${firstSegmentHour}`;
  }

  // After last segment
  if (hour >= lastSegmentHour) {
    return `after-${lastSegmentHour}`;
  }

  // In between - find which bucket
  // Hour H falls into bucket "hour-H" which is between segment H:00 and (H+1):00
  return `hour-${hour}`;
}

/**
 * Distributes pre-created HourlyTaskCard objects into time buckets.
 *
 * Takes already-created HourlyTaskCard objects and distributes them by their
 * hour property. This is useful when cards have already been filtered by status.
 *
 * @param hourlyCards - Array of HourlyTaskCard objects to distribute
 * @param segments - Time segments defining the bucket boundaries
 * @returns Map<bucketId, HourlyTaskCard[]>
 */
export function distributeHourlyCardsToBuckets(
  hourlyCards: HourlyTaskCard[],
  segments: TimeSegment[]
): Map<string, HourlyTaskCard[]> {
  const buckets = new Map<string, HourlyTaskCard[]>();

  // Parse all segment hours and sort
  const segmentHours = segments
    .map(s => parseInt(s.splitTime.split(":")[0], 10))
    .filter(h => !isNaN(h))
    .sort((a, b) => a - b);

  // Initialize all possible buckets (ensures empty buckets exist)
  if (segmentHours.length > 0) {
    const firstHour = segmentHours[0];
    const lastHour = segmentHours[segmentHours.length - 1];

    // Before first segment (e.g., items before 8 AM)
    buckets.set(`before-${firstHour}`, []);

    // Each hour bucket between first and last segment
    for (let h = firstHour; h < lastHour; h++) {
      buckets.set(`hour-${h}`, []);
    }

    // After last segment (e.g., items after 8 PM)
    buckets.set(`after-${lastHour}`, []);
  }

  // Distribute each HourlyTaskCard into the appropriate bucket
  for (const card of hourlyCards) {
    const bucketId = getTimeBucketId(card.hour, segments);

    // Add to bucket (create if doesn't exist)
    const bucket = buckets.get(bucketId);
    if (bucket) {
      bucket.push(card);
    } else {
      buckets.set(bucketId, [card]);
    }
  }

  return buckets;
}
